/**
 * <s-product-gallery> — one-at-a-time product media carousel.
 *
 * Required markup:
 *   [data-gallery-track]  — horizontal scroll-snap container.
 *   [data-gallery-slide]  — one per media item, direct children of the track.
 * Optional:
 *   [data-gallery-prev], [data-gallery-next] — arrow buttons (wrap around).
 *   [data-gallery-status] — live text updated to the current position.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.status = this.querySelector('[data-gallery-status]');
    if (!this.track || this.slides.length < 2) return;

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.goTo(this.#index - 1), opts);
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.goTo(this.#index + 1), opts);
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.target.closest('input, textarea, select, dialog')) return;
        if (event.key === 'ArrowLeft') this.goTo(this.#index - 1);
        else if (event.key === 'ArrowRight') this.goTo(this.#index + 1);
        else return;
        event.preventDefault();
      },
      opts,
    );

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#setIndex(this.slides.indexOf(entry.target));
        }
      },
      { root: this.track, threshold: 0.6 },
    );
    this.slides.forEach((slide) => this.#observer.observe(slide));
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  goTo(index) {
    const count = this.slides.length;
    const next = ((index % count) + count) % count;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: this.slides[next].offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
    this.#setIndex(next);
  }

  #setIndex(index) {
    if (index < 0 || index === this.#index) return;
    this.#index = index;
    if (this.status) {
      this.status.textContent = (this.status.dataset.template || '')
        .replace('[index]', String(index + 1))
        .replace('[count]', String(this.slides.length));
    }
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
